import { memo, useState } from 'react';
import { Link } from 'react-router-dom';
import { Col, Row, Space, Tooltip } from 'antd';
import styled from 'styled-components';
import { dateToString, getDownloadUrl, getFullName } from '@/utils';
import { useGetChildQuery } from '@/services/child-api';
import { DownloadSvg, EditSvg, UserSvg } from '@/assets/icons';
import Panel from '@/components/panel';
import { ProfileCol, ProfileRow } from '@/components/profile-grid';
import Button from '@/components/button';
import CheckItem from '@/components/check-item';
import Photos from './photos';
import StatusModal from './status-modal';
import SummaryPlaceholder from './summary-placeholder';
import SummaryUploader from './summary-uploader';
import SummaryRemover from './summary-remover';
import { useEffect } from 'react';

const Content = styled.div`
  display: flex;
  gap: 32px;

  @media (max-width: 767px) {
    flex-direction: column;
    align-items: center;
  }
`;

const ChildInfo = ({ childId, permissions }) => {
  const { data, isLoading, refetch } = useGetChildQuery({ childId });

  //console.log("akbVideoShownAtChurch: " + data.akbVideoShownAtChurch);

  const [isOpenStatusModal, openStatusModal] = useState(false);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return (
    <Panel
      icon={<UserSvg />}
      title="Child"
      extra={
        permissions.writeChild && (
          <Tooltip title="Edit Child">
            <Link to="../edit">
              <EditSvg fontSize="16" />
            </Link>
          </Tooltip>
        )
      }
      loading={isLoading}
    >
      {data && (
        <Content>
          <Space direction="vertical" size="small">
            <Photos items={data.photos} />
            {permissions.adminStaffAndSuper && (
            <Button block to="../manage-photos">
              Manage Photos
            </Button>
              )}
          </Space> 

          <ProfileRow title={getFullName(data.name)}>
            <ProfileCol sm={12} md={24} lg={12}>
              <ProfileRow>
                <ProfileCol label="Child ID" value={data.childCode} />
                <ProfileCol label="Status">
                  <Space>
                    {data.status.name}
                    {permissions.adminStaffAndSuper && (
                      <Button type="link" onClick={() => openStatusModal(true)}>
                        <EditSvg />
                      </Button>
                    )}
                  </Space>
                  {data.statusReason.name && (
                    <div>
                      <i>Reason: {data.statusReason.name}</i>
                    </div>
                  )}
                </ProfileCol>
                <ProfileCol label="Birthday">
                  {dateToString(data.birthInfo.date)} <i>({data.birthInfo.age} years)</i>
                </ProfileCol>
                <ProfileCol label="Gender" value={data.gender.name} />
                <ProfileCol label="County" value={data.county.name} />
                <ProfileCol label="Date Matched With Adoptive Family">
                  {dateToString(data.dateMatchedWithAdoptiveFamily)}
                </ProfileCol>
                <ProfileCol label="Date Adoption Was Finalized">
                  {dateToString(data.dateAdoptionWasFinalized)}
                </ProfileCol>
              </ProfileRow>
            </ProfileCol>

            <ProfileCol sm={12} md={24} lg={12}>
              <ProfileRow>
                <ProfileCol label="Race/Ethnicity" value={data.race.name} />
                <ProfileCol label="Adoption Liaison">
                  {getFullName(data.adoptionLiaison.name)}
                  <br />
                  <a href={`mainto:${data.adoptionLiaison.emailAddress}`} style={{ fontSize: 12 }}>
                    {data.adoptionLiaison.emailAddress}
                  </a>
                </ProfileCol>
                {data.wwkRecruiter && (
                  <ProfileCol label="WWK Recruiter" style={{ display: 'none' }}>
                    {getFullName(data.wwkRecruiter.name)}
                    <br />
                    <a href={`mainto:${data.wwkRecruiter.emailAddress}`} style={{ fontSize: 12 }}>
                      {data.wwkRecruiter.emailAddress}
                    </a>
                  </ProfileCol>
                )}
                <ProfileCol label="Caseworker">
                  {getFullName(data.caseworker.name)}
                  <br />
                  <a href={`mainto:${data.caseworker.emailAddress}`} style={{ fontSize: 12 }}>
                    {data.caseworker.emailAddress}
                  </a>
                </ProfileCol>
                {data.regionalRecruitmentCoordinator && (
                  <ProfileCol label="Assessor / CS Writer">
                    {getFullName(data.regionalRecruitmentCoordinator.name)}
                    <br />
                    <a href={`mainto:${data.regionalRecruitmentCoordinator.emailAddress}`} style={{ fontSize: 12 }}>
                      {data.regionalRecruitmentCoordinator.emailAddress}
                    </a>
                  </ProfileCol>
                )}
                {data.fcmSupervisor && (
                  <ProfileCol label="FCM Supervisor">
                    {getFullName(data.fcmSupervisor.name)}
                    <br />
                    <a href={`mainto:${data.fcmSupervisor.emailAddress}`} style={{ fontSize: 12 }}>
                      {data.fcmSupervisor.emailAddress}
                    </a>
                  </ProfileCol>
                )}
              </ProfileRow>
            </ProfileCol>

            <ProfileCol label="Child Summary">
              {data.childSummary ? (
                <Space size="middle">
                  <Button
                    type="link"
                    href={getDownloadUrl(`/blobs/${data.childSummary.blobId}/download`)}
                    icon={<DownloadSvg />}
                  >
                    {data.childSummary.fileName}
                  </Button>
                  {permissions.adminStaffAndSuper && <SummaryRemover childId={childId} summaryId={data.childSummary.childSummaryId} onRemove={refetch} />}
                </Space>
              ) : (
                permissions.adminStaffAndSuper && <SummaryUploader childId={childId} onUpload={refetch} />
              )}
            </ProfileCol>
            <ProfileCol>
              <SummaryPlaceholder
                childId={childId}
                defaultValue={data.childSummaryIsPlaceholder}
                disabled={!permissions.adminStaffAndSuper}
              />
            </ProfileCol>

            <ProfileCol label="Bio">{data.bio}</ProfileCol>
            <ProfileCol label="Notes">{data.notes}</ProfileCol>

            <ProfileCol>
              <div style={{ marginBottom: 8 }}>Characteristics</div>
              <Row gutter={24}>
                <Col md={12}>
                  <div>Video</div>
                  <div style={{ paddingLeft: 24 }}>
                    <CheckItem label="Has WNDU segment" value={data.hasWNDU_Video} />
                    <CheckItem label="Has AKB video" value={data.hasAKB_Video} />
                    <CheckItem label="Has GMH video" value={data.hasGMH_Video} />
                    <CheckItem label="AKB Video aired on WTHI" value={data.videoAiredOnWTHI} />
                    <CheckItem label="AKB Video aired on WLKY" value={data.videoAiredOnWLKY} />
                    <CheckItem label="AKB Video aired on WNDU" value={data.akbVideoAiredOnWNDU} />
                    <CheckItem label="AKB Video shown at Church" value={data.akbVideoShownAtChurch} />
                  </div>
                  <div>Case Management</div>
                  <div style={{ paddingLeft: 24 }}>
                    <CheckItem label="Assigned to OYCM" value={data.assignedToOYCM} />
                    <CheckItem label="Plan change + keep active in recruitment" value={data.planChange} />
                    <CheckItem label="Include on AUK" value={data.includeOnAUK} />
                    <CheckItem label="Do not include on AUK – will not consider OOS" value={data.doNotIncludeOnAUK} />
                  </div>
                  <div>Gender</div>
                  <div style={{ paddingLeft: 24 }}>
                    <CheckItem label="LGBTQ+" value={data.lgbtqPlus} />
                    <CheckItem label="Non-binary/gender-fluid" value={data.nonBinaryGenderFluid} />
                  </div>
                  <div>Risk Factors</div>
                  <div style={{ paddingLeft: 24 }}>
                    <CheckItem label="Sexual abuse" value={data.sexualAbuse} />
                    <CheckItem label="History of multiple placements" value={data.historyOfMultiplePlacement} />
                    <CheckItem label="Previously Adopted" value={data.previouslyAdopted} />
                  </div>
                </Col>
                <Col md={12}>
                  <div>Disabilities/Needs</div>
                  <div style={{ paddingLeft: 24 }}>
                    <CheckItem label="Autism" value={data.autism} />
                    <CheckItem label="Non-Verbal" value={data.nonVerbal} />
                    <CheckItem label="Moderate or severe medical" value={data.moderateOrSeverMedical} />
                    <CheckItem label="Moderate or severe developmental" value={data.moderateOrSeverDevelopmental} />
                    <CheckItem label="Moderate emotional" value={data.moderateEmotional} />
                    <CheckItem label="Severe emotional" value={data.severeEmotional} />
                    <CheckItem label="Moderate behavioral" value={data.moderateBehavioral} />
                    <CheckItem label="Severe behavioral" value={data.severeBehavioral} />
                    <CheckItem
                      label="Moderate or severe learning/intellectual"
                      value={data.moderateOrSevereLearningIntellectual}
                    />
                    <CheckItem label={("Other" + (data.other ? `: ${data.other}` : ""))} value={data.other} />
                  </div>
                </Col>
              </Row>
            </ProfileCol>
          </ProfileRow>
        </Content>
      )}

      <StatusModal childId={childId} open={isOpenStatusModal} onSave={refetch} onClose={() => openStatusModal(false)} childSummaryFileName={data?.childSummary?.fileName} />
    </Panel>
  );
};

export default memo(ChildInfo);
